import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

const MatchList = ({ matchData }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [viewCount, setViewCount] = useState(10);

  if (!matchData || !matchData.matches || matchData.matches.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Match History</h3>
        <p className="text-gray-500">No match data available.</p>
      </div>
    );
  }

  const { matches, total_matches } = matchData;
  const displayedMatches = isExpanded ? matches : matches.slice(0, viewCount);

  const formatDate = (dateString) => {
    if (!dateString) return 'Date not available';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getResultColor = (outcome) => {
    switch (outcome) {
      case 'won':
        return 'text-green-600 bg-green-50';
      case 'lost':
        return 'text-red-600 bg-red-50';
      case 'draw':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getMatchTypeColor = (type) => {
    switch (type) {
      case 'League':
        return 'text-blue-600 bg-blue-50';
      case 'Tournament':
        return 'text-purple-600 bg-purple-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatSetDetails = (setDetails) => {
    if (!setDetails || setDetails.length === 0) return '';
    return setDetails.map(score => {
      const absScore = Math.abs(score);
      return score > 0 ? `+${absScore}` : `-${absScore}`;
    }).join(', ');
  };

  const formatLpz = (playerLpz, opponentLpz, lpzChange) => {
    if (playerLpz == null && opponentLpz == null) return null;
    return { ratings: `${playerLpz ?? '?'} vs ${opponentLpz ?? '?'}`, change: lpzChange };
  };

  const getLpzChangeColor = (change) => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-gray-500';
  };

  const getVenueDisplay = (context) => {
    if (context.venue) {
      return context.venue;
    }
    return context.tournament_name ? 'Tournament' : 'Unknown';
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-xl font-semibold text-gray-900">
          Match History ({total_matches} matches)
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Complete chronological record of all matches
        </p>
      </div>
      
      <div className="overflow-x-auto">
        {/* Desktop Table View */}
        <table className="min-w-full divide-y divide-gray-200 hidden md:table">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Opponent
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Result
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Score
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                LPZ
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type & Context
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayedMatches.map((match) => (
              <tr key={match.match_number} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDate(match.date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {match.opponent}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getResultColor(match.outcome)}`}>
                    {match.result}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                  <div>
                    <div>{match.sets || 'N/A'}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {formatSetDetails(match.set_details)}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                  {(() => {
                    const lpz = formatLpz(match.player_lpz, match.opponent_lpz, match.lpz_change);
                    if (!lpz) return <span className="text-gray-400">—</span>;
                    return (
                      <div>
                        <div>{lpz.ratings}</div>
                        {lpz.change != null && (
                          <div className={`text-xs font-semibold mt-1 ${getLpzChangeColor(lpz.change)}`}>
                            {lpz.change > 0 ? `+${lpz.change}` : lpz.change}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getMatchTypeColor(match.type)}`}>
                      {match.type}
                    </span>
                    <div className="space-y-1">
                      {match.type === 'League' ? (
                        <>
                          {match.context.opponent_team && (
                            <div className="text-sm text-gray-900 font-medium">
                              {match.context.opponent_team}
                            </div>
                          )}
                          <div className="text-sm text-gray-600">
                            {match.context.venue || 'Unknown'}{match.context.team_result ? ` - ${match.context.team_result}` : ''}
                          </div>
                        </>
                      ) : (
                        match.context.tournament_name && (
                          <div className="text-sm text-gray-900 font-medium">
                            {match.context.tournament_name}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {/* Mobile Card View */}
        <div className="md:hidden space-y-4 p-4">
          {displayedMatches.map((match) => (
            <div key={match.match_number} className="bg-gray-50 rounded-lg p-4 space-y-3">
              {/* First Row: Date, Opponent, and Result */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-sm text-gray-900">{formatDate(match.date)}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">{match.opponent}</div>
                </div>
                <div>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getResultColor(match.outcome)}`}>
                    {match.result}
                  </span>
                </div>
              </div>
              
              {/* Second Row: Score, LPZ, and Type & Context */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-mono text-gray-900">
                    <div>{match.sets || 'N/A'}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {formatSetDetails(match.set_details)}
                    </div>
                  </div>
                  {(() => {
                    const lpz = formatLpz(match.player_lpz, match.opponent_lpz, match.lpz_change);
                    if (!lpz) return null;
                    return (
                      <div className="mt-2 text-xs font-mono text-gray-600">
                        <div>{lpz.ratings}</div>
                        {lpz.change != null && (
                          <span className={`font-semibold ${getLpzChangeColor(lpz.change)}`}>
                            {lpz.change > 0 ? `+${lpz.change}` : lpz.change}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <div className="space-y-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getMatchTypeColor(match.type)}`}>
                      {match.type}
                    </span>
                    <div className="space-y-1">
                      {match.type === 'League' ? (
                        <>
                          {match.context.opponent_team && (
                            <div className="text-xs text-gray-900 font-medium">
                              {match.context.opponent_team}
                            </div>
                          )}
                          <div className="text-xs text-gray-600">
                            {match.context.venue || 'Unknown'}{match.context.team_result ? ` - ${match.context.team_result}` : ''}
                          </div>
                        </>
                      ) : (
                        match.context.tournament_name && (
                          <div className="text-xs text-gray-900 font-medium">
                            {match.context.tournament_name}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {total_matches > viewCount && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {displayedMatches.length} of {total_matches} matches
            </div>
            <div className="flex space-x-2">
              {!isExpanded && (
                <button
                  onClick={() => setViewCount(prev => Math.min(prev + 10, total_matches))}
                  disabled={viewCount >= total_matches}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Show More
                </button>
              )}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {isExpanded ? (
                  <>
                    <ChevronUpIcon className="w-4 h-4 mr-1" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDownIcon className="w-4 h-4 mr-1" />
                    Show All ({total_matches})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchList;